import { useEffect, useMemo, useState, useRef, FormEvent } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { apiFetch } from '../api';
import { useSmartcard } from '../contexts/SmartcardContext';
import { SmartcardWidget } from '../components/SmartcardWidget';

interface Inventory {
  id: number;
  name: string;
  category_id?: number;
  category_name?: string;
  storage_location_id?: number;
  storage_location_name?: string;
  started_at?: string;
  started_by_user_name?: string;
  completed_at?: string;
  completed_by_user_name?: string;
  notes?: string;
  scan_count?: number;
}

interface ScanRow {
  id: number;
  item_id: number;
  item_name: string;
  scan_method: string;
  scan_code?: string;
  scanned_at: string;
  scanned_by_user_name?: string;
  notes?: string;
  item_condition?: string;
  barcode?: string;
  qr_code?: string;
  rfid_tag?: string;
  nfc_tag?: string;
  sub_location_1?: string;
  sub_location_2?: string;
  sub_location_3?: string;
  sub_location_4?: string;
  expected_quantity?: number;
  actual_quantity?: number;
  quantity_difference?: number;
}

interface MissingRow {
  id: number;
  name: string;
  category_name?: string;
  storage_location_name?: string;
  sub_location_1?: string;
  sub_location_2?: string;
  sub_location_3?: string;
  sub_location_4?: string;
  barcode?: string;
  qr_code?: string;
  rfid_tag?: string;
  nfc_tag?: string;
  quantity?: number;
  missing_quantity?: number;
  missing_type?: 'not_scanned' | 'partial';
}

interface Category { id: number; name: string; }
interface StorageLocation { id: number; name: string; type?: string; active?: boolean; }

export default function StorageInventory() {
  const { id } = useParams<{ id: string }>();
  return id ? <InventoryDetail inventoryId={id} /> : <InventoryList />;
}

function InventoryList() {
  const navigate = useNavigate();
  const [inventories, setInventories] = useState<Inventory[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [locations, setLocations] = useState<StorageLocation[]>([]);
  const [showDialog, setShowDialog] = useState(false);
  const [form, setForm] = useState({ name: '', category_id: '', storage_location_id: '', notes: '' });
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    load();
  }, []);

  async function load() {
    try {
      const [inv, cats, locs] = await Promise.all([
        apiFetch('/admin/storage/inventories'),
        apiFetch('/admin/storage/categories'),
        apiFetch('/admin/storage/locations'),
      ]);
      setInventories(inv || []);
      setCategories(cats || []);
      setLocations((locs || []).filter((l: StorageLocation) => l.active !== false));
    } catch (e: any) {
      setError(e?.message || 'Fehler beim Laden');
    }
  }

  async function createInventory(e: FormEvent) {
    e.preventDefault();
    setError(null);
    try {
      const body: any = {
        name: form.name,
        category_id: form.category_id ? parseInt(form.category_id) : null,
        storage_location_id: form.storage_location_id ? parseInt(form.storage_location_id) : null,
        notes: form.notes,
      };
      const res = await apiFetch('/admin/storage/inventories', { method: 'POST', body: JSON.stringify(body) });
      setShowDialog(false);
      setForm({ name: '', category_id: '', storage_location_id: '', notes: '' });
      await load();
      if (res?.id) navigate(`/storage/inventory/${res.id}`);
    } catch (e: any) {
      setError(e?.message || 'Fehler beim Erstellen');
    }
  }

  async function deleteInventory(invId: number) {
    if (!window.confirm('Soll diese Lager-Inventur wirklich gelöscht werden?')) return;
    setError(null);
    try {
      await apiFetch(`/admin/storage/inventories/${invId}`, { method: 'DELETE' });
      await load();
    } catch (e: any) {
      setError(e?.message || 'Fehler beim Löschen');
    }
  }

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold">📦 Lager-Inventur</h1>
        <button onClick={() => setShowDialog(true)} className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded">➕ Neue Lager-Inventur</button>
      </div>
      {error && <div className="p-3 bg-red-100 text-red-800 rounded">{error}</div>}

      <div className="bg-white dark:bg-gray-800 p-4 rounded border border-gray-200 dark:border-gray-700">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left border-b border-gray-200 dark:border-gray-700">
              <th className="py-2">Name</th>
              <th className="py-2">Kategorie</th>
              <th className="py-2">Lagerort</th>
              <th className="py-2">Gestartet</th>
              <th className="py-2">Status</th>
              <th className="py-2 text-right">Scans</th>
              <th className="py-2 text-right">Aktion</th>
            </tr>
          </thead>
          <tbody>
            {inventories.map((inv) => (
              <tr key={inv.id} className="border-b border-gray-100 dark:border-gray-800">
                <td className="py-2 font-semibold">{inv.name}</td>
                <td className="py-2">{inv.category_name || 'Alle'}</td>
                <td className="py-2">{inv.storage_location_name || 'Alle'}</td>
                <td className="py-2">{inv.started_at ? new Date(inv.started_at).toLocaleString() : '-'}</td>
                <td className="py-2">{inv.completed_at ? 'Abgeschlossen' : 'Laufend'}</td>
                <td className="py-2 text-right">{inv.scan_count || 0}</td>
                <td className="py-2 text-right">
                  <button onClick={() => navigate(`/storage/inventory/${inv.id}`)} className="px-3 py-2 bg-gray-700 hover:bg-gray-800 text-white rounded mr-2">Öffnen</button>
                  <button onClick={() => deleteInventory(inv.id)} className="px-3 py-2 bg-red-600 hover:bg-red-700 text-white rounded">Löschen</button>
                </td>
              </tr>
            ))}
            {inventories.length === 0 && (
              <tr><td className="py-3 text-gray-500 dark:text-gray-400" colSpan={7}>Noch keine Lager-Inventuren</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {showDialog && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 p-6 rounded-lg w-full max-w-lg">
            <h2 className="text-xl font-bold mb-4">Neue Lager-Inventur starten</h2>
            <form onSubmit={createInventory} className="space-y-3">
              <div>
                <label className="block text-sm mb-1">Name *</label>
                <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full px-3 py-2 border rounded bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600" />
              </div>
              <div>
                <label className="block text-sm mb-1">Kategorie (optional)</label>
                <select value={form.category_id} onChange={(e) => setForm({ ...form, category_id: e.target.value })} className="w-full px-3 py-2 border rounded bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600">
                  <option value="">Alle</option>
                  {categories.map((c) => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm mb-1">Lagerort (optional)</label>
                <select value={form.storage_location_id} onChange={(e) => setForm({ ...form, storage_location_id: e.target.value })} className="w-full px-3 py-2 border rounded bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600">
                  <option value="">Alle</option>
                  {locations.map((l) => (
                    <option key={l.id} value={l.id}>{l.type}: {l.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm mb-1">Notiz (optional)</label>
                <textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} className="w-full px-3 py-2 border rounded bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600"></textarea>
              </div>
              <div className="flex gap-2 mt-4">
                <button type="submit" className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded">Starten</button>
                <button type="button" onClick={() => setShowDialog(false)} className="flex-1 px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded">Abbrechen</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

function InventoryDetail({ inventoryId }: { inventoryId: string }) {
  const navigate = useNavigate();
  const [inventory, setInventory] = useState<Inventory | null>(null);
  const [scans, setScans] = useState<ScanRow[]>([]);
  const [missing, setMissing] = useState<MissingRow[]>([]);
  const [tab, setTab] = useState<'scans' | 'missing'>('scans');
  const [error, setError] = useState<string | null>(null);

  const [code, setCode] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const { lastCardData } = useSmartcard();

  // Mengenabfrage-Dialog
  const [showQuantityDialog, setShowQuantityDialog] = useState(false);
  const [pendingScan, setPendingScan] = useState<{ code: string; method: string; item: any } | null>(null);
  const [actualQuantity, setActualQuantity] = useState('');
  
  // Scan-Modus: 'single' = mit Mengenabfrage, 'multi' = automatisches Hochzählen
  const [scanMode, setScanMode] = useState<'single' | 'multi'>('single');
  
  // Inventur-Abschluss-Dialog
  const [showCompleteDialog, setShowCompleteDialog] = useState(false);

  useEffect(() => {
    load();
  }, [inventoryId]);

  useEffect(() => {
    if (lastCardData?.cardNumber) {
      handleScan(lastCardData.cardNumber, 'nfc');
    }
  }, [lastCardData]);

  async function load() {
    try {
      const [inv, sc, miss] = await Promise.all([
        apiFetch(`/admin/storage/inventories/${inventoryId}`),
        apiFetch(`/admin/storage/inventories/${inventoryId}/scans`),
        apiFetch(`/admin/storage/inventories/${inventoryId}/missing`),
      ]);
      setInventory(inv);
      setScans(sc || []);
      setMissing(miss || []);
    } catch (e: any) {
      setError(e?.message || 'Fehler beim Laden');
    }
  }

  async function handleScan(scanCode: string, method = 'barcode') {
    if (!scanCode.trim()) return;
    
    console.log(`[Frontend Scan] Code: ${scanCode}, Mode: ${scanMode}`);
    
    // Input sofort leeren für schnellen nächsten Scan
    setCode('');
    
    setError(null);
    try {
      // Multiscan-Modus: Direkt scannen mit actual_quantity=1 (jeder Scan zählt)
      if (scanMode === 'multi') {
        console.log(`[Frontend Scan] Sending multiscan request...`);
        await apiFetch(`/admin/storage/inventories/${inventoryId}/scan`, {
          method: 'POST',
          body: JSON.stringify({ 
            code: scanCode, 
            scan_method: method,
            actual_quantity: 1, // Jeder Scan = +1
            multi_scan: true // Flag für Multiscan-Modus
          }),
        });
        console.log(`[Frontend Scan] Multiscan complete, reloading...`);
        await load();
        console.log(`[Frontend Scan] Reload complete`);
        return;
      }
      
      // Singlescan-Modus: Mit Mengenabfrage
      const result = await apiFetch(`/admin/storage/inventories/${inventoryId}/scan`, {
        method: 'POST',
        body: JSON.stringify({ code: scanCode, scan_method: method }),
      });
      
      // Prüfe ob Mengenabfrage nötig ist
      if (result?.needsQuantityCheck || (result?.item?.quantity > 1 && !result?.scan?.actual_quantity)) {
        // Zeige Mengenabfrage-Dialog
        setPendingScan({ code: scanCode, method, item: result.item });
        setActualQuantity(String(result.item.quantity));
        setShowQuantityDialog(true);
      } else {
        await load();
      }
    } catch (e: any) {
      setError(e?.message || 'Fehler beim Scannen');
      // Bei Fehler Code zurücksetzen für erneuten Versuch
      setCode(scanCode);
    }
  }

  async function confirmQuantity() {
    if (!pendingScan) return;
    setError(null);
    try {
      const qty = parseFloat(actualQuantity) || 0;
      await apiFetch(`/admin/storage/inventories/${inventoryId}/scan`, {
        method: 'POST',
        body: JSON.stringify({ 
          code: pendingScan.code, 
          scan_method: pendingScan.method,
          actual_quantity: qty
        }),
      });
      setShowQuantityDialog(false);
      setPendingScan(null);
      setActualQuantity('');
      await load();
    } catch (e: any) {
      setError(e?.message || 'Fehler beim Speichern');
    }
  }

  function complete() {
    setShowCompleteDialog(true);
  }

  async function completeOnly() {
    setError(null);
    setShowCompleteDialog(false);
    try {
      await apiFetch(`/admin/storage/inventories/${inventoryId}/complete`, { 
        method: 'POST',
        body: JSON.stringify({ adjustQuantities: false })
      });
      await load();
    } catch (e: any) {
      setError(e?.message || 'Fehler beim Abschließen');
    }
  }

  async function completeWithAdjustment() {
    setError(null);
    setShowCompleteDialog(false);
    try {
      const result = await apiFetch(`/admin/storage/inventories/${inventoryId}/complete`, { 
        method: 'POST',
        body: JSON.stringify({ adjustQuantities: true })
      });
      await load();
      if (result?.adjusted > 0) {
        alert(`✅ Inventur abgeschlossen und ${result.adjusted} Artikel-Bestände angepasst.`);
      }
    } catch (e: any) {
      setError(e?.message || 'Fehler beim Abschließen');
    }
  }

  async function deleteInv() {
    if (!window.confirm('Soll diese Lager-Inventur wirklich gelöscht werden?')) {
      return;
    }
    try {
      await apiFetch(`/admin/storage/inventories/${inventoryId}`, { method: 'DELETE' });
      navigate('/storage/inventory');
    } catch (e: any) {
      setError(e?.message || 'Fehler beim Löschen');
    }
  }

  function exportMissing() {
    const csv = [
      'ID,Name,Kategorie,Lagerort,Status,Fehlende Menge,Barcode,QR-Code,RFID,NFC',
      ...missing.map((m) => {
        const locationPath = [
          m.storage_location_name,
          m.sub_location_1,
          m.sub_location_2,
          m.sub_location_3,
          m.sub_location_4
        ].filter(Boolean).join(' → ');
        
        const status = m.missing_type === 'partial' ? 'Teilweise fehlt' : 'Nicht gescannt';
        const missingQty = m.missing_quantity || m.quantity || 0;
        
        return [
          m.id,
          m.name,
          m.category_name || '',
          locationPath || '',
          status,
          missingQty,
          m.barcode || '',
          m.qr_code || '',
          m.rfid_tag || '',
          m.nfc_tag || '',
        ].join(',');
      }),
    ].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `inventur_${inventoryId}_missing.csv`;
    a.click();
  }

  if (!inventory) return <div className="p-6">Lädt...</div>;

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <button onClick={() => navigate('/storage/inventory')} className="px-3 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded mb-2">← Zurück</button>
          <h1 className="text-3xl font-bold">📦 Lager-Inventur: {inventory.name}</h1>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            {inventory.category_name && `Kategorie: ${inventory.category_name} | `}
            {inventory.storage_location_name && `Lagerort: ${inventory.storage_location_name} | `}
            Gestartet: {inventory.started_at ? new Date(inventory.started_at).toLocaleString() : '-'}
          </p>
        </div>
        <div className="flex gap-2">
          {!inventory.completed_at && (
            <button onClick={complete} className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded mr-2">Lager-Inventur beenden</button>
          )}
          <button onClick={deleteInv} className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded">Löschen</button>
        </div>
      </div>

      {error && <div className="p-3 bg-red-100 text-red-800 rounded">{error}</div>}

      {!inventory.completed_at && (
        <div className="bg-white dark:bg-gray-800 p-4 rounded border border-gray-200 dark:border-gray-700">
          <h2 className="text-lg font-bold mb-3">🔍 Artikel scannen</h2>
          
          {/* Scan-Modus Auswahl */}
          <div className="mb-4 flex items-center gap-4 p-3 bg-gray-50 dark:bg-gray-900 rounded border border-gray-200 dark:border-gray-700">
            <span className="font-semibold text-sm">Scan-Modus:</span>
            <label className="flex items-center gap-2 cursor-pointer">
              <input 
                type="radio" 
                checked={scanMode === 'single'} 
                onChange={() => setScanMode('single')}
                className="w-4 h-4 cursor-pointer"
              />
              <span className="text-sm">
                <span className="font-semibold">Singlescan</span>
                <span className="text-gray-500 dark:text-gray-400 ml-1">(mit Mengenabfrage)</span>
              </span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input 
                type="radio" 
                checked={scanMode === 'multi'} 
                onChange={() => setScanMode('multi')}
                className="w-4 h-4 cursor-pointer"
              />
              <span className="text-sm">
                <span className="font-semibold">Multiscan</span>
                <span className="text-gray-500 dark:text-gray-400 ml-1">(jeder Scan = +1)</span>
              </span>
            </label>
          </div>
          
          <div className="flex gap-2">
            <input
              ref={inputRef}
              value={code}
              onChange={(e) => setCode(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleScan(code)}
              placeholder="Barcode, QR-Code, RFID oder NFC scannen..."
              className={`flex-1 px-3 py-2 rounded bg-white dark:bg-gray-800 border-4 animate-pulse ${
                scanMode === 'single' 
                  ? 'border-yellow-400 dark:border-yellow-500' 
                  : 'border-cyan-400 dark:border-cyan-500'
              }`}
              autoFocus
            />
            <button onClick={() => handleScan(code)} className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded">Scannen</button>
          </div>
          <SmartcardWidget className="mt-2" />
        </div>
      )}

      <div className="flex gap-2 mb-2">
        <button onClick={() => setTab('scans')} className={`px-4 py-2 rounded ${tab === 'scans' ? 'bg-blue-600 text-white' : 'bg-gray-200 dark:bg-gray-700'}`}>
          ✅ Gescannt ({scans.length})
        </button>
        <button onClick={() => setTab('missing')} className={`px-4 py-2 rounded ${tab === 'missing' ? 'bg-blue-600 text-white' : 'bg-gray-200 dark:bg-gray-700'}`}>
          ❌ Fehlend ({missing.length})
        </button>
      </div>

      {tab === 'scans' && (
        <div className="bg-white dark:bg-gray-800 p-4 rounded border border-gray-200 dark:border-gray-700">
          <h2 className="text-lg font-bold mb-2">✅ Gescannte Artikel ({scans.length})</h2>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left border-b border-gray-200 dark:border-gray-700">
                  <th className="py-2">Artikel</th>
                  <th className="py-2">Methode</th>
                  <th className="py-2">Code</th>
                  <th className="py-2">Soll</th>
                  <th className="py-2">Ist</th>
                  <th className="py-2">Differenz</th>
                  <th className="py-2">Zeitpunkt</th>
                  <th className="py-2">Gescannt von</th>
                </tr>
              </thead>
              <tbody>
                {scans.map((s) => {
                  const hasQuantityTracking = s.expected_quantity !== null && s.expected_quantity !== undefined;
                  const hasDifference = s.quantity_difference !== null && s.quantity_difference !== undefined && s.quantity_difference !== 0;
                  
                  return (
                  <tr key={s.id} className={`border-b border-gray-100 dark:border-gray-800 ${hasDifference ? 'bg-yellow-50 dark:bg-yellow-900/20' : ''}`}>
                    <td className="py-2">{s.item_name}</td>
                    <td className="py-2">{s.scan_method}</td>
                    <td className="py-2 font-mono text-xs">{s.scan_code || '-'}</td>
                    <td className="py-2">
                      {hasQuantityTracking ? (
                        <span className="font-bold text-blue-600 dark:text-blue-400">{s.expected_quantity}</span>
                      ) : '-'}
                    </td>
                    <td className="py-2">
                      {s.actual_quantity !== null && s.actual_quantity !== undefined ? (
                        <span className="font-bold text-green-600 dark:text-green-400">{s.actual_quantity}</span>
                      ) : '-'}
                    </td>
                    <td className="py-2">
                      {hasDifference ? (
                        <span className="font-bold text-red-600 dark:text-red-400">
                          {s.quantity_difference}
                        </span>
                      ) : '-'}
                    </td>
                    <td className="py-2">{new Date(s.scanned_at).toLocaleString()}</td>
                    <td className="py-2">{s.scanned_by_user_name || '-'}</td>
                  </tr>
                  );
                })}
                {scans.length === 0 && <tr><td colSpan={8} className="py-3 text-gray-500 dark:text-gray-400">Noch keine Scans</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === 'missing' && (
        <div className="bg-white dark:bg-gray-800 p-4 rounded border border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-lg font-bold">❌ Fehlende Artikel ({missing.length})</h2>
            {missing.length > 0 && (
              <button onClick={exportMissing} className="px-3 py-2 bg-green-600 hover:bg-green-700 text-white rounded text-sm">📥 CSV Export</button>
            )}
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left border-b border-gray-200 dark:border-gray-700">
                  <th className="py-2">Name</th>
                  <th className="py-2">Kategorie</th>
                  <th className="py-2">Lagerort</th>
                  <th className="py-2">Status</th>
                  <th className="py-2">Fehlende Menge</th>
                  <th className="py-2">Barcode</th>
                </tr>
              </thead>
              <tbody>
                {missing.map((m) => {
                  // Vollständigen Lagerort-Pfad erstellen
                  const locationPath = [
                    m.storage_location_name,
                    m.sub_location_1,
                    m.sub_location_2,
                    m.sub_location_3,
                    m.sub_location_4
                  ].filter(Boolean).join(' → ');
                  
                  const isPartial = m.missing_type === 'partial';
                  const displayQuantity = m.missing_quantity || m.quantity || 0;
                  
                  return (
                  <tr key={m.id} className={`border-b border-gray-100 dark:border-gray-800 ${isPartial ? 'bg-yellow-50 dark:bg-yellow-900/20' : 'bg-red-50 dark:bg-red-900/20'}`}>
                    <td className="py-2">{m.name}</td>
                    <td className="py-2">{m.category_name || '-'}</td>
                    <td className="py-2">{locationPath || '-'}</td>
                    <td className="py-2">
                      {isPartial ? (
                        <span className="px-2 py-1 text-xs rounded bg-yellow-200 dark:bg-yellow-700 text-yellow-900 dark:text-yellow-100">Teilweise fehlt</span>
                      ) : (
                        <span className="px-2 py-1 text-xs rounded bg-red-200 dark:bg-red-700 text-red-900 dark:text-red-100">Nicht gescannt</span>
                      )}
                    </td>
                    <td className="py-2 font-semibold">{displayQuantity}</td>
                    <td className="py-2 font-mono text-xs">{m.barcode || '-'}</td>
                  </tr>
                  );
                })}
                {missing.length === 0 && <tr><td colSpan={6} className="py-3 text-gray-500 dark:text-gray-400">Alle Artikel gescannt! 🎉</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Inventur-Abschluss-Dialog */}
      {showCompleteDialog && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-lg w-full p-6">
            <h2 className="text-xl font-bold mb-4">✅ Inventur abschließen</h2>
            <p className="mb-6 text-sm text-gray-600 dark:text-gray-400">
              Wie möchten Sie diese Inventur abschließen?
            </p>
            <div className="space-y-3 mb-6">
              <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded border-2 border-blue-200 dark:border-blue-800">
                <h3 className="font-bold text-blue-900 dark:text-blue-100 mb-2">📊 Bestände anpassen (empfohlen)</h3>
                <p className="text-sm text-gray-700 dark:text-gray-300 mb-3">
                  Die gezählten IST-Mengen werden als neue SOLL-Werte ins Lagersystem übernommen. 
                  Nur gescannte Artikel werden aktualisiert.
                </p>
                <button 
                  onClick={completeWithAdjustment} 
                  className="w-full px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded font-semibold"
                >
                  Inventur beenden + Bestände anpassen
                </button>
              </div>
              <div className="p-4 bg-gray-50 dark:bg-gray-900 rounded border-2 border-gray-200 dark:border-gray-700">
                <h3 className="font-bold text-gray-900 dark:text-white mb-2">🔒 Nur Inventur beenden</h3>
                <p className="text-sm text-gray-700 dark:text-gray-300 mb-3">
                  Die Inventur wird als abgeschlossen markiert, aber die Lagerbestände werden NICHT geändert.
                </p>
                <button 
                  onClick={completeOnly} 
                  className="w-full px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded"
                >
                  Nur beenden (ohne Anpassung)
                </button>
              </div>
            </div>
            <button 
              onClick={() => setShowCompleteDialog(false)} 
              className="w-full px-4 py-2 bg-gray-500 hover:bg-gray-600 text-white rounded"
            >
              Abbrechen
            </button>
          </div>
        </div>
      )}

      {/* Mengenabfrage-Dialog */}
      {showQuantityDialog && pendingScan && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full p-6">
            <h2 className="text-xl font-bold mb-4">📦 Mengenabfrage</h2>
            <p className="mb-4 text-sm text-gray-600 dark:text-gray-400">
              <strong>{pendingScan.item.name}</strong>
            </p>
            <div className="bg-blue-50 dark:bg-blue-900/20 p-3 rounded mb-4">
              <p className="text-sm">
                <strong>Soll-Menge (im System):</strong> <span className="font-bold text-blue-600 dark:text-blue-400">{pendingScan.item.quantity}</span>
              </p>
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium mb-2">
                <span>Tatsächlich gefundene Menge (Ist): </span>
                <span className="text-green-600 dark:text-green-400 font-bold">?</span>
              </label>
              <input
                type="number"
                value={actualQuantity}
                onChange={(e) => setActualQuantity(e.target.value)}
                min="0"
                step="0.01"
                className="w-full px-3 py-2 border rounded bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600"
                autoFocus
                onKeyDown={(e) => e.key === 'Enter' && confirmQuantity()}
              />
            </div>
            {actualQuantity && parseFloat(actualQuantity) !== pendingScan.item.quantity && (
              <div className={`p-3 rounded mb-4 ${parseFloat(actualQuantity) < pendingScan.item.quantity ? 'bg-red-50 dark:bg-red-900/20' : 'bg-green-50 dark:bg-green-900/20'}`}>
                <p className="text-sm font-bold text-red-600 dark:text-red-400">
                  Differenz: {pendingScan.item.quantity - parseFloat(actualQuantity)} 
                  {parseFloat(actualQuantity) < pendingScan.item.quantity ? ' fehlen' : ' zu viel'}
                </p>
              </div>
            )}
            <div className="flex gap-2">
              <button onClick={confirmQuantity} className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded">Bestätigen</button>
              <button onClick={() => { setShowQuantityDialog(false); setPendingScan(null); setActualQuantity(''); }} className="flex-1 px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded">Abbrechen</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
